// Pure helpers for the GPX elevation-profile / map-hover feature in
// NajSportEvents.tsx (GpxMapModal). Extracted so the distance-search and
// elevation-detection logic can be unit-tested without mounting Leaflet/recharts.

/**
 * Given a monotonic non-decreasing array of cumulative distances (km), one
 * per parsed GPX point, find the index whose distance is closest to `target`.
 *
 * This replaces a naive `round((target / totalDistance) * (points.length - 1))`
 * ratio, which assumes points are spaced evenly by index. Real GPX tracks are
 * not: a rider who stops produces a dense cluster of points at ~the same
 * distance, and a fast descent produces a sparse run of points covering a lot
 * of distance quickly. The ratio approach drifts to the wrong point in both
 * cases; a distance-based binary search does not.
 *
 * @returns -1 for an empty array; otherwise an index into `cumDist`.
 */
export function nearestIndexByDistance(cumDist: number[], target: number): number {
  const n = cumDist.length;
  if (n === 0) return -1;
  if (n === 1) return 0;
  if (target <= cumDist[0]) return 0;
  if (target >= cumDist[n - 1]) return n - 1;

  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cumDist[mid] <= target) lo = mid;
    else hi = mid;
  }
  // target now lies between cumDist[lo] and cumDist[hi]; pick whichever is
  // numerically closer, preferring the lower index on an exact tie.
  return Math.abs(cumDist[lo] - target) <= Math.abs(cumDist[hi] - target) ? lo : hi;
}

/**
 * Whether a parsed GPX track has at least one usable <ele> reading. Some
 * tracks (e.g. plain route exports) carry no elevation data at all; the UI
 * should not render an elevation badge/gain for those.
 */
export function hasFiniteElevation(elevations: number[]): boolean {
  return elevations.some((e) => Number.isFinite(e));
}
